import { Component, DestroyRef, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { API, FileLink, HtmlDTO, MarkerDTO, TemplateStoreDTO } from 'src/app/services/api/api';
import { ErrorService } from 'src/app/services/error.service';
import { UtilService } from 'src/app/services/util.service';
import { EmailEditorComponent } from 'src/app/addons/unlayer/angular-email-editor/email-editor.component';
import { ThemingService } from '@ngx-seatback/utils';
import { TranslateService } from '@ngx-translate/core';
import { ContentEditorService } from 'src/app/services/content-editor/content-editor.service';
import { ImageViewerComponent } from 'src/app/components/controls/image-viewer/image-viewer.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalService } from 'src/app/services/gui/modal/modal.service';
import { SbBookableMarkersComponent } from 'src/app/components/sb-bookable-markers/sb-bookable-markers.component';
import { PermissionService } from '../../../services/permission.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';
import { SbWizardComponent, SbWizardTabComponent } from '@ngx-seatback/layout';

@Component({
  selector: 'app-content-editor',
  templateUrl: './content-editor.component.html',
  styleUrls: ['./content-editor.component.scss'],
  standalone: false,
})
export class ContentEditorComponent implements OnInit {
  loading: boolean;
  editmode: boolean;
  debounceTimer: ReturnType<typeof setTimeout> = null;
  debounceTimerEditorLoad: ReturnType<typeof setTimeout> = null;

  templateId: number;

  template: TemplateStoreDTO;

  showEditor: boolean = false;
  showPreviewButton: boolean = false;

  form = new UntypedFormGroup({
    Name: new UntypedFormControl('', [Validators.required]),
    Description: new UntypedFormControl(''),
  });

  get Name() {
    return this.form.get('Name');
  }

  get Description() {
    return this.form.get('Description');
  }

  previewForm = new UntypedFormGroup({
    Width: new UntypedFormControl(800, [Validators.required]),
    Height: new UntypedFormControl(600, [Validators.required]),
  });

  get Width() {
    return this.previewForm.get('Width');
  }

  get Height() {
    return this.previewForm.get('Height');
  }

  @ViewChild(EmailEditorComponent)
  private emailEditor: EmailEditorComponent;

  projectId: number = 36210;
  displayMode: 'web' | 'email' = 'web';
  locale: string = 'de-DE';
  dockSide: 'left' | 'right' = 'left';
  theme: 'dark' | 'light' = 'light';
  editorPreview: boolean = false;
  stockImages: any = false;

  minHeight = '640px';

  user: string;
  signature: string;
  loadEditor: boolean = false;

  constructor(
    private api: API,
    private activatedRoute: ActivatedRoute,
    public util: UtilService,
    private errorHandler: ErrorService,
    public themingService: ThemingService,
    public translate: TranslateService,
    private contentEditorService: ContentEditorService,
    private ngbModal: NgbModal,
    private modal: ModalService,
    public permissionService: PermissionService,
    private destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    this.activatedRoute.params.subscribe(params => {
      this.templateId = +params.contentEditorID;
      if (this.templateId) {
        this.editmode = true;
        this.requestDebounced();
      } else {
        this.loading = true;
        this.editmode = false;
        this.template = new TemplateStoreDTO();
        this.init();
      }
    });
  }

  init() {
    this.themingService.theme.subscribe((theme: string) => {
      switch (theme) {
        case 'dark-theme':
          this.applyTheme('dark');
          break;
        case 'light-theme':
        default:
          this.applyTheme('light');
          break;
      }
    });

    this.applyLocale();
    this.translate.onDefaultLangChange.subscribe(() => {
      this.applyLocale();
    });

    this.api
      .shared_Client_retrieveEditorSignature()
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (res: any) => {
          this.user = res?.id;
          this.signature = res?.signature;
          this.applyOptions();
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });

    this.loading = false;
  }

  refreshFormFromDTO() {
    this.Name.setValue(this.template.name);
    this.Description.setValue(this.template.description);
  }

  refreshDTOFromForm() {
    this.template.name = this.Name.value;
    this.template.description = this.Description.value;
  }

  requestDebounced() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.load();
    }, 500);
  }

  selectMarker() {
    this.modal
      .showNavigationModal({
        entryComponent: { value: SbBookableMarkersComponent, data: {} },
        title: null,
        modalOptions: {
          // windowClass: 'bigXL',
          size: 'xl',
        },
      })
      .then((result: MarkerDTO) => {
        if (result && result.id && result.resolution) {
          this.Width.setValue(result.resolution.width);
          this.Height.setValue(result.resolution.height);
        }
      })
      .catch(() => {});
  }

  load() {
    this.loading = true;
    this.editmode = true;
    this.api
      .mm_templating_store_TemplateStore_getTemplateStore(this.templateId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (res: TemplateStoreDTO) => {
          this.template = res;
          this.refreshFormFromDTO();
          this.init();
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  cancel() {
    this.util.returnToNavigation('/content/media/content-editor');
  }

  nextStep(wizard: SbWizardComponent, nextTab: SbWizardTabComponent) {
    wizard.selectTab(nextTab);
  }

  submit() {
    this.loading = true;
    this.saveDesign();
  }

  preview() {
    this.showPreview(this.Width.value, this.Height.value);
  }

  showPreview(width: number, height: number) {
    this.loading = true;

    this.emailEditor.editor.saveDesign(() => {
      this.emailEditor.editor.exportHtml((htmlData: any) => {
        const htmlDTO: HtmlDTO = this.contentEditorService.createHtmlDTO(
          htmlData?.html ? this.contentEditorService.injectCustomCSSToEditorData(htmlData).html : '',
          width,
          height,
          null
        );

        this.api
          .mm_Medium_uploadFileHtmlPreview(htmlDTO)
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            take(1),
            finalize(() => {
              this.loading = false;
            })
          )
          .subscribe({
            next: (result: FileLink) => {
              if (result) {
                const modal = this.ngbModal.open(ImageViewerComponent, { size: 'lg' });
                (modal.componentInstance as ImageViewerComponent).title = this.translate.instant('lang.globals.preview');
                if (this.Name?.value) {
                  (modal.componentInstance as ImageViewerComponent).title +=
                    ' ' + this.translate.instant('lang.contentEditor.template') + ' ' + this.Name.value;
                }
                (modal.componentInstance as ImageViewerComponent).title += ' ' + '(' + htmlDTO.width + 'x' + htmlDTO.height + ' px' + ')';
                (modal.componentInstance as ImageViewerComponent).src = result.data ? result.data : result;
                (modal.componentInstance as ImageViewerComponent).modalRef = modal;
              }
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      });
    });
  }

  editorLoaded() {
    setTimeout(() => {
      if (this.template?.value) {
        this.emailEditor.loadDesign(JSON.parse(this.template.value));
      }
      this.showPreviewButton = true;
    }, 1500);
  }

  exportHtml() {
    this.emailEditor.editor.exportHtml(data => {
      console.log('exportHtml', this.contentEditorService.injectCustomCSSToEditorData(data));
    });
  }

  saveDesign() {
    this.emailEditor.editor.saveDesign(data => {
      this.template.value = JSON.stringify(data);
      this.refreshDTOFromForm();
      this.submitToServer();
    });
  }

  submitToServer() {
    if (this.editmode) {
      this.api
        .mm_templating_store_TemplateStore_editTemplateStore(this.template)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: () => {
            this.cancel();
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    } else {
      this.api
        .mm_templating_store_TemplateStore_createTemplateStore(this.template)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: () => {
            this.cancel();
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  applyLocale() {
    if (this.translate.currentLang === 'de') {
      this.locale = 'de-DE';
    } else {
      this.locale = 'en-US';
    }
    this.reloadEditor();
  }

  applyTheme(theme: 'dark' | 'light') {
    this.theme = theme;
    this.reloadEditor();
  }

  applyOptions() {
    this.reloadEditor();
  }

  reloadEditor() {
    this.loadEditor = false;

    if (this.showEditor) {
      if (this.debounceTimerEditorLoad) {
        clearTimeout(this.debounceTimerEditorLoad);
      }
      this.debounceTimerEditorLoad = setTimeout(() => {
        this.loadEditor = true;
      }, 500);
    }
  }
}
